import { type PartialDeep } from 'type-fest';

import {
  type FormContainerVariantType,
  type IoConfig,
  type PropsOf,
  type WithColumns,
  type WithIoConfig,
  type WithProps,
} from '@blaze-runtime/engine-domain';
import { DEFAULT_NO_DATA_MESSAGE } from '@blaze-shared/elements-domain';
import { DEFAULT_SCHEMA } from '@blaze-shared/schema-domain';

import { type CanvasElementDefinition } from '../../canvas-elements/element-definition';
import type { ParentChildrenOverrides } from '../../canvas-elements/parent-overrides';
import { registerElementDefinition } from '../../services/element-definitions.service';

import { FormContainerDefinitionMapper } from './ai/form-container.definition-mapper';
import { FormContainerElementHandler } from './ai/form-container.element-handler';
import { FormDetailsDefinitionMapper } from './ai/form-details.definition-mapper';
import { FormDetailsElementHandler } from './ai/form-details.element-handler';
import { FormsChildrenHandlerService } from './form-children-handler.service';
import { FormContainerOutputEmitGuard } from './form-container-output-emit.guard';
import { FormContainerComponent } from './form-container.component';
import {
  FORM_BINDING_KEY,
  FORM_PADDING_DRAGGED,
  type FormContainerPropsModel,
} from './form-container.model';
import { FormContainerPropsBuilder } from './form-container.props-builder';
import { getPropsConfig } from './form-container.props-config';
import { FormContainerPropsExtractor } from './form-container.props-extractor';
import { formContainerV2Migration } from './migrations/002-form-container.migration';
import { formContainerV3Migration } from './migrations/003-form-container.migration';
import { formContainerV4Migration } from './migrations/004-form-container.migration';

export type FormContainerDef = WithColumns & WithProps<FormContainerPropsModel> & WithIoConfig;

// Extend the CanvasElementDefinitions type
declare module '@blaze-runtime/engine-domain' {
  interface CanvasElementDefinitions {
    formContainer: FormContainerDef;
    formDetails: FormContainerDef;
  }
}

const INITIAL_SLOT_ID = '0';
const FORM_CONTAINER_WIDTH = 380;
const FORM_CONTAINER_HEIGHT = 420;

//#region variant: edit-form

export function getFormContainerDefinition(): CanvasElementDefinition<'formContainer'> {
  return {
    type: 'formContainer',
    componentType: FormContainerComponent,
    outputEmitGuard: FormContainerOutputEmitGuard,
    childrenHandler: FormsChildrenHandlerService,
    isListed: true,
    name: 'Form',
    width: { mode: 'full', value: FORM_CONTAINER_WIDTH, fixed: true },
    // By default the element should have a min-height and grow automatically if needed
    height: { mode: 'value', value: FORM_CONTAINER_HEIGHT, fixed: false },
    slots: {
      ids: [INITIAL_SLOT_ID],
      [INITIAL_SLOT_ID]: {
        children: [],
      },
    },
    ioConfig: getIoConfig(),
    propsConfig: getPropsConfig('formContainer'),
    propsBuilder: FormContainerPropsBuilder,
    propsExtractor: FormContainerPropsExtractor,
    initial: getInitialValues('formContainer'),
    defaults: getDefaults(),
    tags: ['form'],
    groups: ['forms'],
    styleIncludePaths: [
      'style',
      'content.title.align',
      'content.title.iconPosition',
      'content.children',
    ],
    metaUiFeatures: { type: 'container' },
    features: {
      addRelatedWidgetSupport: true,
      preventAutoHeight: true,
      resizeWithChild: true,
      visualStyle: 'block',
      ai: {
        description: true,
      },
    },
    ai: {
      name: 'formContainer',
      handler: () => FormContainerElementHandler,
      definitionMapper: () => FormContainerDefinitionMapper,
    },
    childrenOverridesResolver: getChildrenOverridesResolver(),
    version: 4,
    migrations: {
      2: formContainerV2Migration,
      3: formContainerV3Migration,
      4: formContainerV4Migration,
    },
  };
}

registerElementDefinition(getFormContainerDefinition());

//#endregion

//#region variant: details-view

export function getDetailsViewDefinition(): CanvasElementDefinition<
  'formDetails',
  'formContainer'
> {
  return {
    type: 'formDetails',
    variantOf: 'formContainer',
    componentType: FormContainerComponent,
    childrenHandler: FormsChildrenHandlerService,
    isListed: true,
    name: 'Details View',
    width: { mode: 'full', value: FORM_CONTAINER_WIDTH, fixed: true },
    // By default the element should have a min-height and grow automatically if needed
    height: { mode: 'value', value: FORM_CONTAINER_HEIGHT, fixed: false },
    slots: {
      ids: [INITIAL_SLOT_ID],
      [INITIAL_SLOT_ID]: {
        children: [],
      },
    },
    ioConfig: getIoConfig(),
    propsConfig: getPropsConfig('formDetails'),
    propsBuilder: FormContainerPropsBuilder,
    propsExtractor: FormContainerPropsExtractor,
    initial: getInitialValues('formDetails'),
    defaults: getDefaults(),
    tags: ['Form'],
    styleIncludePaths: [
      'style',
      'content.title.align',
      'content.title.iconPosition',
      'content.children',
    ],
    metaUiFeatures: { type: 'container' },
    features: {
      addRelatedWidgetSupport: true,
      preventAutoHeight: true,
      resizeWithChild: true,
      visualStyle: 'block',
      ai: {
        showAiPromptAutomatically: true,
        description: true,
      },
    },
    ai: {
      name: 'formDetails',
      handler: () => FormDetailsElementHandler,
      definitionMapper: () => FormDetailsDefinitionMapper,
    },
    childrenOverridesResolver: getChildrenOverridesResolver(),
    version: 4,
    migrations: {
      2: formContainerV2Migration,
      3: formContainerV3Migration,
      4: formContainerV4Migration,
    },
  };
}

registerElementDefinition(getDetailsViewDefinition());

//#endregion

/** The form's "Style All Fields" / "Format All Fields" bundle is stored on its own props. */
function getChildrenOverridesResolver<
  T extends FormContainerVariantType,
>(): CanvasElementDefinition<T>['childrenOverridesResolver'] {
  return (props) =>
    ({
      content: props.content?.children,
      style: props.style?.children,
    }) as ParentChildrenOverrides;
}

function getIoConfig(): IoConfig {
  return {
    inputs: [
      {
        name: FORM_BINDING_KEY,
        isDefaultDataSourceInput: true,
        isDefaultDataValueInput: true,
        bindingDefs: [],
      },
      {
        name: 'visible',
        bindingDefs: [{ property: 'visible', label: 'Is Visible', dataType: 'Boolean' }],
      },
    ],
    outputs: [
      { eventType: 'change', schema: DEFAULT_SCHEMA },
      { eventType: 'action', schema: DEFAULT_SCHEMA },
    ],
  };
}

function getDefaults(): PropsOf<'formContainer'> {
  return {
    content: {
      origin: 'drag',
      model: [],
      title: {
        enabled: false,
        align: 'left',
        label: '',
        iconColor: { color1: '$t.title', selectorType: 'solid' },
      },
      postAction: {
        actionType: 'None',
      },
      formActions: [],
      headerActions: [],
      actionsView: 'bottom',
      noDataMessage: DEFAULT_NO_DATA_MESSAGE,
      options: {
        columnsCount: 1,
        actionsLayout: 'stacked',
      },
      children: {
        label: {
          position: 'left',
          align: 'left',
          width: '35%',
          widthMode: 'fixed',
        },
        size: {
          width: { mode: 'full' },
          height: { mode: 'value' },
        },
      },
    },
    style: {
      container: {
        title: { color1: '$t.title', selectorType: 'solid' },
        offset: { margin: '$t.spacingExternal', padding: '$t.spacingInternal' },
        fill: { color1: '$t.fillInternal', selectorType: 'none' },
        outlineWidth: '$t.containerOutlineWidth',
        outline: { color1: '$t.outline', selectorType: '$t.containerOutlineType' as any },
        radius: '$t.radius',
        visualEffect: {
          type: '$t.effectType',
          data: {
            x: '$t.effectX',
            y: '$t.effectY',
            blur: '$t.effectBlur',
            color1: '$t.effectColor1',
          },
        },
        noData: {
          minHeight: 200,
        },
      },
      titleBlock: {
        fontStyle: '$t.headerFontStyle' as any,
        fontSize: '$t.headerFontSize',
        fontFamily: '$t.headerFontFamily',
        fontWeight: '$t.headerFontWeight',
        iconSize: '$t.headerIconSize',
      },
      children: {
        titleLabel: {
          color: { color1: '$t.title', selectorType: 'none' },
          fontFamily: '$t.fontFamily',
          style: [],
          fontSize: '$t.fontSize',
        },
        fieldLabel: {
          color: { color1: '$t.title', selectorType: 'none' },
          fontFamily: '$t.fontFamily',
          style: [],
          fontSize: '$t.fontSize',
        },
        fieldBox: {
          accent: { color1: '$t.accent', selectorType: 'none' },
          fill: { color1: '$t.fillInternal', selectorType: 'none' },
          outline: { color1: '$t.outline', selectorType: 'none' },
          outlineWidth: '1px',
          radius: '$t.radius',
        },
      },
    },
  };
}

function getInitialValues(
  formType: FormContainerVariantType,
): PartialDeep<PropsOf<'formContainer'>> {
  const isDetailsView = formType === 'formDetails';

  return {
    content: {
      action: isDetailsView ? 'view' : 'insert',
      origin: 'drag',
      title: {
        enabled: true,
        label: isDetailsView ? 'Details' : 'Form',
      },
      options: {
        scrollToError: true,
        markRequiredFields: true,
        ...(isDetailsView ? undefined : { markRequiredFields: true }),
      },
    },
    style: {
      container: {
        fill: { color1: '$t.fillExternal', selectorType: 'solid' },
        offset: { padding: FORM_PADDING_DRAGGED },
        noData: {
          minHeight: 0,
        },
      },
    },
    featuresConfig: {
      fieldsLabelGen: 2,
    },
  };
}
